import { ArrowDown, ArrowUp, Info } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";

import type { GeoStateRank } from "@busright/data-contract";

import { EmptyState } from "@/components/EmptyState";
import { formatInt, formatScore } from "@/lib/format";

// Per-state ranking from /api/geo/states. Honestly labeled: this is geoip carrier-region, NOT
// precise bus location (the hex map uses real GPS; this leaderboard is ISP-gateway attribution).

type SortKey = "sessionCount" | "avgInaccuracyMeters" | "lowAccuracyPct";

interface ColumnDef {
  readonly key: SortKey;
  readonly header: string;
}

const COLUMNS: readonly ColumnDef[] = [
  { key: "sessionCount", header: "Pings" },
  { key: "avgInaccuracyMeters", header: "Avg inacc. (m)" },
  { key: "lowAccuracyPct", header: "Low-acc %" },
];

const sortValue = (row: GeoStateRank, key: SortKey): number => {
  const v = row[key];
  return v === null ? Number.NEGATIVE_INFINITY : v;
};

export interface GeoStatesPanelProps {
  readonly rows: readonly GeoStateRank[];
  readonly loading?: boolean;
  // Currently focused carrier-region (state code), or null. Drives the row highlight.
  readonly selectedState?: string | null;
  // Click a row to focus that state; clicking the focused row again clears it.
  readonly onSelectState?: (state: string | null) => void;
}

export const GeoStatesPanel = ({
  rows,
  loading,
  selectedState = null,
  onSelectState,
}: GeoStatesPanelProps): ReactNode => {
  const [sortKey, setSortKey] = useState<SortKey>("sessionCount");

  const sorted = useMemo(
    () => rows.slice().sort((a, b) => sortValue(b, sortKey) - sortValue(a, sortKey)),
    [rows, sortKey],
  );

  if (!loading && rows.length === 0) {
    return <EmptyState title="No state ranking" message="geo-states returned no rows." />;
  }

  return (
    <div className="geo-states">
      <p className="geo-states-caveat">
        <Info size={12} aria-hidden /> By carrier-region (geoip) — not precise location. The map
        above uses real GPS; this leaderboard is ISP-gateway attribution only.
      </p>
      <div className="geo-states-scroll">
        <table className="data-table">
          <thead>
            <tr>
              <th>State</th>
              {COLUMNS.map((col) => {
                const active = sortKey === col.key;
                return (
                  <th key={col.key} className="num">
                    <button
                      type="button"
                      className={active ? "geo-sort active" : "geo-sort"}
                      onClick={() => setSortKey(col.key)}
                    >
                      {col.header}
                      {active ? (
                        <ArrowDown size={11} aria-hidden />
                      ) : (
                        <ArrowUp size={11} aria-hidden className="geo-sort-ghost" />
                      )}
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sorted.map((row) => {
              const selected = selectedState === row.state;
              const selectable = typeof onSelectState === "function";
              const toggle = (): void => onSelectState?.(selected ? null : row.state);
              const cls = ["geo-states-row"];
              if (selectable) {
                cls.push("focusable");
              }
              if (selected) {
                cls.push("selected");
              }
              return (
                <tr
                  key={row.state}
                  className={cls.join(" ")}
                  onClick={selectable ? toggle : undefined}
                >
                  <td>{row.state}</td>
                  <td className="num br-tabular-nums">{formatInt(row.sessionCount)}</td>
                  <td className="num br-tabular-nums">{formatScore(row.avgInaccuracyMeters, 0)}</td>
                  <td className="num br-tabular-nums">{formatScore(row.lowAccuracyPct, 1)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
